"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Who sees it",
    rule: "Settings → Preferences and advanced → Organization defaults. Title Default Organization Configuration. View permission (or admin). Save / Reset need edit",
  },
  {
    topic: "Cascade",
    rule: "Division required, then Branch (cleared when Division changes), then Warehouse filtered to that branch (cleared when Branch changes). All three required to Save Configuration",
  },
  {
    topic: "Reset to None",
    rule: "Clears division, branch, and warehouse. Disabled until a default division is already saved",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [{ heading: "1. Default Division, Branch, Warehouse" }],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function OrganizationDefaultsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Organization Defaults
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins (and roles with this permission) — pre-fill division,
            branch, and warehouse on new documents
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>Default Organization Configuration</B>. Guide on screen:
            These settings will be automatically applied when creating new
            documents. You can still change them individually for each
            document if needed.
          </p>
          <Flow
            steps={[
              "Settings",
              "Organization defaults",
              "Division → Branch → Warehouse",
              "Save Configuration",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Default Division, Branch, Warehouse
          </h3>
          <Definitions
            items={[
              {
                term: "Fields",
                desc: "Default Division, Default Branch, Default Warehouse — all required to save",
              },
              {
                term: "What it affects",
                desc: "Pre-filled organisation location on new documents. Users can still change the pickers on the document. It does not move existing documents",
              },
            ]}
          />
          <Steps
            items={[
              <>
                Pick Division, then Branch, then Warehouse.
              </>,
              <>
                Click <B>Save Configuration</B>. Or <B>Reset to None</B> to
                clear all three.
              </>,
            ]}
          />
          <Callout type="note">
            Warehouse options are only warehouses on the selected branch.
          </Callout>
          <Screenshot caption="Default Organization Configuration — Division, Branch, Warehouse, Reset to None, Save Configuration" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/all-master/division"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Division <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Master list these defaults pick from
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/inventory-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              When stock moves — not which warehouse is the default
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
